import { useEffect, useRef, useState } from "react";
import {
    MapContainer,
    TileLayer,
    Marker,
    Polyline,
    CircleMarker,
    Popup,
    useMapEvents,
    useMap,
} from "react-leaflet";
import L from "leaflet";
import { useLang } from "../contexts/LanguageContext";

// Base layers available in the toolbar layer selector. `i18n` is the
// translation key for the label (OpenStreetMap keeps its proper name).
export const BASE_LAYERS = [
    {
        key: "osm",
        label: "OpenStreetMap",
        i18n: null,
        url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
        attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        maxZoom: 19,
    },
    {
        key: "sat",
        i18n: "layerSat",
        url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
        attribution: "Tiles &copy; Esri",
        maxZoom: 19,
    },
    {
        key: "topo",
        i18n: "layerTopo",
        url: "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png",
        attribution:
            '&copy; <a href="https://opentopomap.org">OpenTopoMap</a> (CC-BY-SA)',
        maxZoom: 17,
    },
    {
        key: "cycle",
        i18n: "layerCycle",
        url: "https://{s}.tile-cyclosm.openstreetmap.fr/cyclosm/{z}/{x}/{y}.png",
        attribution: '&copy; <a href="https://www.cyclosm.org">CyclOSM</a>',
        maxZoom: 20,
    },
    {
        key: "dark",
        i18n: "layerDark",
        url: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
        attribution: '&copy; <a href="https://carto.com">CARTO</a>',
        maxZoom: 19,
    },
];

const PAVED_COLOR = "#3b82f6"; // asphalt → blue
const UNPAVED_COLOR = "#ff7a18"; // unpaved / unknown → orange

// Group the track into consecutive same-surface runs for colored rendering.
function buildColoredSegments(points) {
    const segs = [];
    let cur = null;
    for (let i = 1; i < points.length; i++) {
        const color = points[i].paved === true ? PAVED_COLOR : UNPAVED_COLOR;
        const a = [points[i - 1].lat, points[i - 1].lng];
        const b = [points[i].lat, points[i].lng];
        if (cur && cur.color === color) {
            cur.positions.push(b);
        } else {
            cur = { color, positions: [a, b] };
            segs.push(cur);
        }
    }
    return segs;
}

// Below this zoom level the individual track/POI markers are hidden so the
// map stays clean when looking at the whole route; they reappear when close.
const MARKER_MIN_ZOOM = 13;

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl:
        "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
    iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
    shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

function makeWaypointIcon(index, total) {
    let cls = "wp-marker";
    if (index === 0) cls += " start";
    else if (index === total - 1 && total > 1) cls += " end";
    const label = index === 0 ? "A" : index === total - 1 ? "B" : index;
    return L.divIcon({
        className: "wp-icon-wrap",
        html: `<div class="${cls}" data-testid="waypoint-marker-${index}">${label}</div>`,
        iconSize: [14, 14],
        iconAnchor: [7, 7],
    });
}

function makePoiIcon() {
    return L.divIcon({
        className: "poi-icon-wrap",
        html: `<div class="poi-marker"><i class="fa-solid fa-location-dot"></i></div>`,
        iconSize: [18, 18],
        iconAnchor: [9, 9],
    });
}

function ClickHandler({ onMapClick }) {
    useMapEvents({
        click(e) {
            onMapClick(e.latlng);
        },
    });
    return null;
}

function ZoomWatcher({ onZoom }) {
    const map = useMapEvents({
        zoomend() {
            onZoom(map.getZoom());
        },
    });
    return null;
}

function FlyTo({ target }) {
    const map = useMap();
    useEffect(() => {
        if (!target) return;
        if (target.bbox && target.bbox.length === 4) {
            const [s, n, w, e] = target.bbox;
            map.fitBounds([
                [s, w],
                [n, e],
            ]);
        } else {
            map.flyTo([target.lat, target.lng], Math.max(map.getZoom(), 13), {
                duration: 1.0,
            });
        }
    }, [target, map]);
    return null;
}

function FitBounds({ points }) {
    const map = useMap();
    const lastFitRef = useRef(0);
    useEffect(() => {
        if (!points || points.length < 2) return;
        // Only auto-fit when count changes significantly (eg. after import)
        if (Math.abs(points.length - lastFitRef.current) >= 5) {
            const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
            map.fitBounds(bounds.pad(0.2));
        }
        lastFitRef.current = points.length;
    }, [points, map]);
    return null;
}

// Exposes the Leaflet map instance to the parent so the toolbar can drive
// zoom in / out from its own buttons.
function MapReady({ onReady }) {
    const map = useMap();
    useEffect(() => {
        if (onReady) onReady(map);
    }, [map, onReady]);
    return null;
}

export default function MapView({
    points,
    pois,
    mode,
    overlayTracks,
    onAddPoint,
    onAddPoi,
    onInsertPoint,
    onMovePoint,
    onMovePoi,
    onRemovePoint,
    onRemovePoi,
    onEditPoi,
    flyTarget,
    hoverPoint,
    activeLayer = "osm",
    onMapReady,
    initialCenter = [40.4168, -3.7038],
    initialZoom = 6,
}) {
    const { t } = useLang();
    const mapRef = useRef(null);
    const [zoom, setZoom] = useState(initialZoom);
    const showMarkers = zoom >= MARKER_MIN_ZOOM;

    const handleClick = (latlng) => {
        if (mode === "track") onAddPoint({ lat: latlng.lat, lng: latlng.lng });
        else if (mode === "poi")
            onAddPoi({
                lat: latlng.lat,
                lng: latlng.lng,
                name: t("poiDefault"),
                desc: "",
            });
    };

    // Click on the track line to insert a waypoint at the nearest segment.
    const insertOnLine = (e) => {
        if (mode !== "track" || !onInsertPoint) return;
        const clickLL = e.latlng;
        const map = e.target._map;
        const clickPt = map.latLngToLayerPoint(clickLL);
        let bestIdx = 1;
        let bestDist = Infinity;
        for (let i = 1; i < points.length; i++) {
            const a = map.latLngToLayerPoint([points[i - 1].lat, points[i - 1].lng]);
            const b = map.latLngToLayerPoint([points[i].lat, points[i].lng]);
            const d = pointToSegment(clickPt, a, b);
            if (d < bestDist) {
                bestDist = d;
                bestIdx = i;
            }
        }
        onInsertPoint(bestIdx, { lat: clickLL.lat, lng: clickLL.lng });
        L.DomEvent.stopPropagation(e);
    };

    return (
        <div
            data-testid="map-container"
            style={{ height: "100%", width: "100%" }}
        >
            <MapContainer
                center={initialCenter}
                zoom={initialZoom}
                style={{ height: "100%", width: "100%" }}
                zoomControl={false}
                ref={mapRef}
            >
                {(() => {
                    const layer =
                        BASE_LAYERS.find((l) => l.key === activeLayer) ||
                        BASE_LAYERS[0];
                    return (
                        <TileLayer
                            key={layer.key}
                            attribution={layer.attribution}
                            url={layer.url}
                            maxZoom={layer.maxZoom}
                        />
                    );
                })()}

                <MapReady onReady={onMapReady} />

                <ClickHandler onMapClick={handleClick} />
                <ZoomWatcher onZoom={setZoom} />
                <FlyTo target={flyTarget} />
                <FitBounds points={points} />

                {/* Overlay other saved tracks - faint */}
                {overlayTracks &&
                    overlayTracks.map((t) =>
                        t.points && t.points.length > 1 ? (
                            <Polyline
                                key={`ov-${t.id}`}
                                positions={t.points.map((p) => [p.lat, p.lng])}
                                pathOptions={{
                                    color: "#60a5fa",
                                    weight: 3,
                                    opacity: 0.55,
                                    dashArray: "6 6",
                                }}
                            />
                        ) : null,
                    )}

                {points.length > 1 &&
                    buildColoredSegments(points).map((seg, si) => (
                        <Polyline
                            key={`seg-${si}`}
                            positions={seg.positions}
                            pathOptions={{
                                color: seg.color,
                                weight: 4,
                                opacity: 0.92,
                                lineCap: "round",
                                lineJoin: "round",
                            }}
                            eventHandlers={{ click: insertOnLine }}
                        />
                    ))}

                {hoverPoint && (
                    <CircleMarker
                        center={[hoverPoint.lat, hoverPoint.lng]}
                        radius={8}
                        pathOptions={{
                            color: "#ffffff",
                            weight: 3,
                            fillColor: "#ff2d55",
                            fillOpacity: 1,
                        }}
                        data-testid="elevation-hover-marker"
                    />
                )}

                {showMarkers &&
                    points.map((p, i) => (
                    <Marker
                        key={p.id}
                        position={[p.lat, p.lng]}
                        icon={makeWaypointIcon(i, points.length)}
                        draggable
                        eventHandlers={{
                            dragend: (e) => {
                                const ll = e.target.getLatLng();
                                onMovePoint(p.id, {
                                    lat: ll.lat,
                                    lng: ll.lng,
                                });
                            },
                        }}
                    >
                        <Popup>
                            <div className="text-sm">
                                <div className="font-semibold mb-1">
                                    {t("point", { n: i + 1 })}
                                </div>
                                <div className="text-xs opacity-80">
                                    {p.lat.toFixed(5)}, {p.lng.toFixed(5)}
                                </div>
                                {typeof p.ele === "number" && (
                                    <div className="text-xs opacity-80">
                                        {t("altitudeLabel", { n: Math.round(p.ele) })}
                                    </div>
                                )}
                                <button
                                    className="rf-btn danger mt-2"
                                    onClick={() => onRemovePoint(p.id)}
                                    data-testid={`remove-point-${i}`}
                                >
                                    <i className="fa-solid fa-trash"></i>{" "}
                                    {t("remove")}
                                </button>
                            </div>
                        </Popup>
                    </Marker>
                ))}

                {showMarkers &&
                    pois.map((p) => (
                    <Marker
                        key={p.id}
                        position={[p.lat, p.lng]}
                        icon={makePoiIcon()}
                        draggable
                        eventHandlers={{
                            dragend: (e) => {
                                const ll = e.target.getLatLng();
                                onMovePoi(p.id, { lat: ll.lat, lng: ll.lng });
                            },
                        }}
                    >
                        <Popup>
                            <div
                                className="text-sm"
                                style={{ minWidth: 220 }}
                            >
                                <label className="block text-[10px] uppercase tracking-wider opacity-60 mb-1">
                                    {t("name")}
                                </label>
                                <input
                                    className="rf-input mb-2"
                                    defaultValue={p.name}
                                    onBlur={(e) =>
                                        onEditPoi(p.id, {
                                            name: e.target.value,
                                        })
                                    }
                                    data-testid={`poi-name-${p.id}`}
                                />
                                <label className="block text-[10px] uppercase tracking-wider opacity-60 mb-1">
                                    {t("notes")}
                                </label>
                                <textarea
                                    className="rf-input mb-2"
                                    rows={3}
                                    defaultValue={p.desc}
                                    onBlur={(e) =>
                                        onEditPoi(p.id, {
                                            desc: e.target.value,
                                        })
                                    }
                                    data-testid={`poi-desc-${p.id}`}
                                />
                                <button
                                    className="rf-btn danger w-full justify-center"
                                    onClick={() => onRemovePoi(p.id)}
                                    data-testid={`remove-poi-${p.id}`}
                                >
                                    <i className="fa-solid fa-trash"></i>{" "}
                                    {t("removePoi")}
                                </button>
                            </div>
                        </Popup>
                    </Marker>
                ))}
            </MapContainer>
        </div>
    );
}

function pointToSegment(p, a, b) {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    if (dx === 0 && dy === 0) {
        const ddx = p.x - a.x;
        const ddy = p.y - a.y;
        return Math.sqrt(ddx * ddx + ddy * ddy);
    }
    let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / (dx * dx + dy * dy);
    t = Math.max(0, Math.min(1, t));
    const fx = a.x + t * dx;
    const fy = a.y + t * dy;
    return Math.sqrt((p.x - fx) ** 2 + (p.y - fy) ** 2);
}
